import { useEffect, useState } from "react";
import { log } from "../utils/logger";
import { costCategories } from "../data/chapters";
import { DollarSign, Calculator, TrendingUp, ShieldCheck, FileSignature, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDON_SERVICES = [
  {
    key: "registered_agent",
    icon: ShieldCheck,
    title: "Registered Agent Service",
    subtitle: "1-year coverage",
    description: "We act as your Texas registered agent for 12 months. Includes physical Texas address and mail forwarding.",
    price: 75,
    testId: "buy-registered-agent",
  },
  {
    key: "operating_agreement",
    icon: FileSignature,
    title: "Operating Agreement Drafting",
    subtitle: "Delivered in 3 business days",
    description: "We draft a customized Operating Agreement for your Texas LLC, tailored to your members and operations.",
    price: 50,
    testId: "buy-operating-agreement",
  },
];

export default function CostEstimator({ values, onChange }) {
  const [loadingKey, setLoadingKey] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [applyCredit, setApplyCredit] = useState({});
  const total = Object.values(values).reduce((a, b) => a + (Number(b) || 0), 0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancel) setCreditBalance(Number(data.credit_balance || 0));
      } catch (err) {
        log.warn("Credit balance unavailable (not signed in?)", err);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const handleChange = (key, val) => {
    onChange({ ...values, [key]: Number(val) || 0 });
  };

  const handleBuy = async (productKey) => {
    setLoadingKey(productKey);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        {
          origin_url: window.location.origin,
          product: productKey,
          apply_credit: !!applyCredit[productKey],
        },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setLoadingKey(null);
    }
  };

  const diyTotal = 300;

  return (
    <div data-testid="cost-estimator" className="space-y-8">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl border-2 border-[var(--c-border)] bg-[#A3E6D5] shadow-[3px_3px_0px_var(--c-shadow)]">
          <Calculator className="h-7 w-7 text-[#1A1918]" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[var(--c-text)] sm:text-3xl">
          Startup Cost Estimator
        </h3>
        <p className="mt-2 text-[var(--c-muted)]">
          Estimate your total cost to form a Texas LLC
        </p>
      </div>

      {/* Cost Items */}
      <div className="space-y-4">
        {costCategories.map((cat) => (
          <div
            key={cat.key}
            data-testid={`cost-item-${cat.key}`}
            className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-heading text-base font-bold text-[var(--c-text)]">{cat.label}</h4>
                <p className="mt-1 text-sm text-[var(--c-muted)]">{cat.description}</p>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4 text-[var(--c-muted)]" />
                {cat.fixed ? (
                  <span className="text-lg font-bold text-[var(--c-text)]">{cat.min}</span>
                ) : (
                  <input
                    data-testid={`cost-input-${cat.key}`}
                    type="number"
                    min={cat.min}
                    max={cat.max}
                    value={values[cat.key] ?? cat.min}
                    onChange={(e) => handleChange(cat.key, e.target.value)}
                    className="w-20 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2 text-right text-lg font-bold text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20"
                  />
                )}
              </div>
            </div>
            {!cat.fixed && (
              <div className="mt-3">
                <input
                  data-testid={`cost-slider-${cat.key}`}
                  type="range"
                  min={cat.min}
                  max={cat.max}
                  value={values[cat.key] ?? cat.min}
                  onChange={(e) => handleChange(cat.key, e.target.value)}
                  className="w-full accent-[#1E3A8A]"
                />
                <div className="flex justify-between text-xs text-[var(--c-muted)]">
                  <span>${cat.min}</span>
                  <span>${cat.max}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Your Total */}
      <div className="rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A] p-6 shadow-[6px_6px_0px_var(--c-shadow)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">
              Your Estimated Total
            </p>
            <p
              data-testid="cost-total"
              className="font-heading text-4xl font-black text-white sm:text-5xl"
            >
              ${total}
            </p>
          </div>
          <TrendingUp className="h-10 w-10 text-white/60" />
        </div>
      </div>

      {/* Comparison */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border-2 border-[var(--c-border)] bg-[#A3E6D5]/20 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
            DIY / Low-Cost
          </p>
          <p className="font-heading text-2xl font-bold text-[var(--c-text)]">${diyTotal} - $400</p>
          <p className="mt-1 text-sm text-[var(--c-muted)]">
            Do everything yourself to minimize costs
          </p>
        </div>
        <div className="rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A]/10 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
            Mid-Range (Most Common)
          </p>
          <p className="font-heading text-2xl font-bold text-[var(--c-text)]">$450 - $950+</p>
          <p className="mt-1 text-sm text-[var(--c-muted)]">
            Mix of DIY and professional services
          </p>
        </div>
      </div>

      <div className="rounded-xl border-2 border-dashed border-[var(--c-border-medium)] bg-[var(--c-bg)] p-5 text-center">
        <p className="font-heading text-base font-bold text-[var(--c-text)]">
          Start Smart, Not Expensive
        </p>
        <p className="mt-1 text-sm text-[var(--c-muted)]">
          Focus on filing properly, staying compliant, and keeping your finances separate. You can always upgrade services as your business grows.
        </p>
      </div>

      {/* Done-for-you Add-on Services */}
      <div data-testid="addon-services" className="space-y-4">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">Done-for-you Services</p>
          <h3 className="font-heading text-xl font-bold text-[var(--c-text)] sm:text-2xl">Skip the Hassle — Let Us Handle It</h3>
          <p className="mt-1 text-sm text-[var(--c-muted)]">Pay once. We take care of the paperwork.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ADDON_SERVICES.map((svc) => {
            const Icon = svc.icon;
            const loading = loadingKey === svc.key;
            const useCredit = !!applyCredit[svc.key];
            const creditApplied = useCredit ? Math.min(creditBalance, Math.max(svc.price - 1, 0)) : 0;
            const finalPrice = Math.max(svc.price - creditApplied, 0);
            return (
              <div
                key={svc.key}
                data-testid={`addon-card-${svc.key}`}
                className="flex flex-col rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[4px_4px_0px_var(--c-shadow)]"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-base font-bold text-[var(--c-text)]">{svc.title}</p>
                    <p className="text-xs text-[var(--c-muted)]">{svc.subtitle}</p>
                  </div>
                  <div className="text-right">
                    {creditApplied > 0 ? (
                      <>
                        <p className="text-xs font-bold text-[var(--c-muted)] line-through">${svc.price}</p>
                        <p className="font-heading text-2xl font-black text-[#1E3A8A]">${finalPrice.toFixed(2)}</p>
                      </>
                    ) : (
                      <p className="font-heading text-2xl font-black text-[var(--c-text)]">${svc.price}</p>
                    )}
                  </div>
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[var(--c-text)]">{svc.description}</p>
                {creditBalance > 0 && (
                  <label
                    data-testid={`apply-credit-${svc.key}`}
                    className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#A3E6D5] bg-[#A3E6D5]/10 p-2 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={useCredit}
                      onChange={(e) => setApplyCredit((s) => ({ ...s, [svc.key]: e.target.checked }))}
                      className="h-4 w-4 accent-[#1E3A8A]"
                    />
                    <Sparkles className="h-3.5 w-3.5 text-[#1E3A8A]" />
                    <span className="font-bold text-[var(--c-text)]">Apply ${Math.min(creditBalance, Math.max(svc.price - 1, 0)).toFixed(2)} referral credit</span>
                  </label>
                )}
                <button
                  data-testid={svc.testId}
                  onClick={() => handleBuy(svc.key)}
                  disabled={loading || loadingKey !== null}
                  className="mt-4 flex items-center justify-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-2.5 font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)] disabled:opacity-60"
                >
                  {loading ? "Redirecting…" : (
                    <>
                      Buy for ${finalPrice.toFixed(2)}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-[var(--c-muted)]">Secure payment via Stripe &middot; You&apos;ll be contacted within 1 business day to start fulfillment</p>
      </div>
    </div>
  );
}
